<template>
	<div
		class="moment-tile"
		:class="{ 'is-playing': isPlaying }"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
		@touchstart="toggleTouch"
	>
		<img
			v-if="backgroundImage"
			v-show="imageLoaded"
			:src="backgroundImage"
			class="moment-tile__background-image"
			:class="{ '-loaded': imageLoaded }"
			@load="onImageLoad"
		>
		<video
			ref="video"
			class="moment-tile__player"
			muted
			playsinline
			:src="videoUrl"
			:poster="backgroundImage"
		/>
		<div class="moment-tile__inner">
			<div class="moment-tile__header">
				<div class="moment-tile__tag-w">
					<div
						v-if="expiresAt"
						class="moment-tile__tag"
					>
						{{ expiresAt }}
					</div>
					<div
						v-if="isCommunityCrop"
						class="moment-tile__tag"
					>
						<Icon
							icon-name="multiple-2"
							size="sm"
						/>
					</div>
				</div>
				<div
					class="moment-tile__download"
					@click.stop="$emit('download')"
					@touchstart.stop="$emit('download')"
				>
					<Icon
						icon-name="data-download"
						size="sm"
					/>
				</div>
			</div>
			<div
				v-if="name"
				class="moment-tile__footer"
			>
				<div class="moment-tile__title-w">
					<!-- eslint-disable vue/no-v-html -->
					<h5
						class="moment-tile__title"
						v-html="name"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon';
import laroute from '@laroute';

export default {
	name:       'MomentTile',
	components: { Icon },
	props:      {
		id: {
			type:     [String, Number],
			required: true,
		},
		name: {
			type:    String,
			default: null,
		},
		backgroundImage: {
			type:    String,
			default: null,
		},
		expiresAt: {
			type:    String,
			default: null,
		},
		isCommunityCrop: {
			type:     Boolean,
			required: true,
		},
	},
	data() {
		return {
			videoUrl:    '',
			imageLoaded: false,
			isPlaying:   false,
		};
	},
	methods: {
		onImageLoad() {
			this.imageLoaded = true;
		},
		toggleTouch() {
			if (this.isPlaying) {
				this.handleMouseLeave();
			} else {
				this.handleMouseEnter();
			}
		},
		async handleMouseEnter() {
			const video = this.$refs.video;
			if (!this.videoUrl) {
				await this.getVideoUrl();
			}
			video.play();
			this.isPlaying = true;
		},
		handleMouseLeave() {
			const video = this.$refs.video;
			video.pause();
			video.currentTime = 0;
			this.isPlaying = false;
		},
		async getVideoUrl() {
			try {
				const options = {
					method: 'GET',
					url:    laroute.route('api.event_video_crop.playout', { eventVideoCrop: this.id }),
				};

				const response = await fetch(options.url, options);
				const data = await response.json();

				const videoUrl = data?.videoUrl;
				if (videoUrl) {
					this.videoUrl = videoUrl;
				}
			} catch (error) {
				console.error('Error fetching video URL:', error);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.moment-tile {
	aspect-ratio: 9/16;
	border-radius: $border-radius--lg;
	overflow: hidden;
	color: var(--color-white);
	background-color: var(--color-grey-300);
	background-size: cover;
	position: relative;
	width: 100%;
	height: 100%;
}

.moment-tile__inner {
	position: relative;
	width: 100%;
	height: 100%;
	padding: $sp5 $sp4;
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	&::after { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		content: '';
		display: block;
		position: absolute;
		left: 0;
		bottom: 0;
		width: 100%;
		height: 50%;
		background: linear-gradient(180deg, rgba(var(--color-rgb-black), 0) 0%, var(--color-black) 100%);
		opacity: 0.8;
		z-index: 0;

		@media (hover: hover) {
			.moment-tile:hover & {
				display: none;
			}
		}

		@include is-touch {
			.moment-tile.is-playing & {
				display: none;
			}
		}
	}
}

.moment-tile__background-image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	opacity: 0;
	transition: opacity $trans-time-fast;

	&.-loaded {
		opacity: 1;
	}
}

.moment-tile__player {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
}

.moment-tile__header {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: $sp2;
}

.moment-tile__footer {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	z-index: 2;
}

.moment-tile__title-w {
	opacity: 1;
	transition: opacity $trans-time-fast;

	@media (hover: hover) {
		.moment-tile:hover & {
			opacity: 0;
		}
	}

	@include is-touch {
		.moment-tile.is-playing & {
			opacity: 0;
		}
	}
}

.moment-tile__title {
	@include font-size(fs-80);
	color: var(--color-grey-200);
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box; // stylelint-disable-line value-no-vendor-prefix
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical; // stylelint-disable-line property-no-vendor-prefix
}

.moment-tile__tag-w {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	opacity: 1;
	transition: opacity $trans-time-fast;

	@media (hover: hover) {
		.moment-tile:hover & {
			opacity: 0;
		}
	}

	@include is-touch {
		.moment-tile.is-playing & {
			opacity: 0;
		}
	}
}

.moment-tile__tag {
	@include font-size(fs-60);
	border-radius: $border-radius--sm;
	background-color: rgba(var(--color-rgb-black), 0.6);
	padding: 0 $sp2;
	height: 20px;
	display: flex;
	align-items: center;
	white-space: nowrap;
}

.moment-tile__download {
	position: absolute;
	top: 0;
	right: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	border-radius: $border-radius--sm;
	color: var(--color-white);
	background-color: rgba(var(--color-rgb-black), 0.6);
	opacity: 0;
	transition: opacity $trans-time-fast;
	cursor: pointer;

	@media (hover: hover) {
		.moment-tile:hover & {
			opacity: 1;
		}
	}

	@include is-touch {
		.moment-tile.is-playing & {
			opacity: 1;
		}
	}
}
</style>
